import { canUseDOM } from "@sproutsocial/seeds-react-utilities";
import { useEffect, useMemo, useState } from "react";

type TypeMutationObserverInitRequired =
  | {
      childList: true;
    }
  | {
      attributes: true;
    }
  | {
      characterData: true;
    };

type TypeMutationObserverInit = {
  subtree?: boolean;
  attributeOldValue?: boolean;
  characterDataOldValue?: boolean;
  attributeFilter?: Array<string>;
} & TypeMutationObserverInitRequired;

type TypeMutationObserverCallback = (
  mutationList?: MutationRecord[],
  observer?: MutationObserver
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
) => any;

const defaultCallback: TypeMutationObserverCallback = (mutationList) =>
  mutationList;

export function useMutationObserver(
  targetNode: Node | null,
  config: TypeMutationObserverInit,
  callback: TypeMutationObserverCallback = defaultCallback
) {
  if (!canUseDOM()) {
    return;
  }
  /* eslint-disable-next-line */
  const [value, setValue] = useState(undefined);
  /* eslint-disable-next-line */
  const observer = useMemo(
    () =>
      new MutationObserver((mutationList, observer) => {
        const result = callback(mutationList, observer);
        setValue(result);
      }),
    [callback]
  );
  /* eslint-disable-next-line */
  useEffect(() => {
    if (targetNode) {
      observer.observe(targetNode, config);
      return () => {
        observer.disconnect();
      };
    }
  }, [targetNode, config, observer]);

  return value;
}

export function useMutationObserverOnce(
  targetNode: Node | null,
  config: TypeMutationObserverInit,
  callback: TypeMutationObserverCallback
) {
  const [isObserving, setObserving] = useState(true);
  const node = isObserving ? targetNode : null;
  const value = useMutationObserver(node, config, callback);
  if (value !== undefined && isObserving) {
    setObserving(false);
  }
  return value;
}
